import { createFileRoute, notFound } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { isUuid } from '@leanin/shared';
import { useMember, useAuthedSession } from '@/lib/use-session';
import { eventsKeys } from '@/lib/queries/events';
import { groupKeys } from '@/lib/queries/groups';
import { adaptEventDetail } from '@/components/events/event-adapters';
import type { EventDetail as EventDetailDomain } from '@leanin/shared/views/event';
import { isNotFoundApiError } from '@/lib/api-error';
import { NotFoundPage } from '@/components/shell/NotFoundPage';
import { EditEventForm } from './-components/EditEventForm';

export const Route = createFileRoute('/_authed/events/$id/edit/')({
  beforeLoad: ({ params }) => {
    if (!isUuid(params.id)) throw notFound();
  },
  component: EditEventPage,
});

function EditEventPage() {
  const { id } = Route.useParams();
  const member = useMember();
  const session = useAuthedSession();

  const detailQuery = useQuery(eventsKeys.detail(id));

  const detail = detailQuery.data;
  const isOrganizer =
    detail != null &&
    session != null &&
    detail.organizerUserId !== null &&
    detail.organizerUserId === session.userId;
  const isPlatformAdmin = session?.platformRole === 'admin';
  const needsGroupRole =
    detail != null && !isOrganizer && !isPlatformAdmin && detail.groupId != null;

  const groupRoleQuery = useQuery({
    ...groupKeys.viewerRole(detail?.groupId ?? ''),
    enabled: needsGroupRole,
  });

  if (isNotFoundApiError(detailQuery.error)) return <NotFoundPage />;
  if (member === null || detail == null) return null;
  if (needsGroupRole && groupRoleQuery.isLoading) return null;

  // Calendar-synced events are managed externally — edit is not available.
  if (detail.source === 'calendar') return <NotFoundPage />;

  const isLeader = groupRoleQuery.data === 'leader';
  const canManage = isOrganizer || isPlatformAdmin || isLeader;
  if (!canManage) return <NotFoundPage />;

  const event = adaptEventDetail(detail as unknown as EventDetailDomain);
  return <EditEventForm eventId={id} event={event} />;
}
